import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Card,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Divider,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CardActions,
  CircularProgress,
  Fade,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Clear as ClearIcon,
  Schedule as ScheduleIcon,
  Tag as TagIcon,
  Sort as SortIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Transform as TransformIcon,
} from '@mui/icons-material';
import {
  addProjectToStorage,
  updateDealInStorage,
} from '../utils/localStorage';
import { createDeal, getDeals, deleteDeal } from '../services/api';
import { Deal, DealStatus, DealType, DealSort, ProjectLifecycle, ProjectStage, ProjectPriority } from '../types';
import CreateDealDialog from '../components/CreateDealDialog';
import DealCard from '../components/DealCard';
import { DealCardSkeleton } from '../components/LoadingSkeleton';

// Mock data for past deals
// const pastDeals = [
//   {
//     id: '1',
//     name: 'Willow Creek Apartments',
//     location: 'Plainsboro, NJ',
//     size: '88 units',
//     vintage: 'Built 1983',
//     dealType: 'Core Plus',
//     closedDate: 'Closed 8 months ago',
//   },
//   {
//     id: '2',
//     name: 'Parkside Commons',
//     location: 'Boston, MA',
//     size: '72 units',
//     vintage: 'Built 1992',
//     dealType: 'Value Add',
//     closedDate: 'Closed 14 months ago',
//   },
// ];

// Mock data for pro forma fields

const toTitleCase = (str: string) => {
  if (!str) return '';
  return str
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const DealSourcing: React.FC = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [page] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [dealType, setDealType] = useState<DealType | ''>('');
  const [status, setStatus] = useState<DealStatus | ''>('');
  const [sector] = useState<string[]>([]);
  const [region] = useState<string[]>([]);
  const [sort, setSort] = useState<DealSort>(DealSort.CREATED_DESC);
  const [selectedDeal, setSelectedDeal] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Create project dialog state
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [selectedDealForProject, setSelectedDealForProject] = useState<any>(null);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');

  // Create deal dialog state
  const [createDealDialogOpen, setCreateDealDialogOpen] = useState(false);

  // Convert to project state
  const [convertingDeals, setConvertingDeals] = useState<Set<string>>(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [dealToDelete, setDealToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Initialize storage with mock data on component mount
  const fetchDeals = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDeals({
        page,
        size: pageSize,
        search: search || undefined,
        deal_type: dealType ? [dealType] : undefined,
        status: status ? [status] : undefined,
        sector: sector.length > 0 ? sector : undefined,
        region: region.length > 0 ? region : undefined,
        sort,
      });
      setDeals(data.deals);
    } catch (error: any) {
      console.error('Failed to fetch deals:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, dealType, status, sector, region, sort, setLoading, setDeals]);

  useEffect(() => {
    // Debounce the API call to prevent excessive requests
    const timeoutId = setTimeout(() => {
      fetchDeals();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, pageSize, search, dealType, status, sector, region, sort, fetchDeals]);


  const handleDealSelect = (dealId: string) => {
    setSelectedDeal(selectedDeal === dealId ? null : dealId);
  };

  const handleCreateProjectSubmit = () => {
    if (!selectedDealForProject || !projectName.trim()) return;

    // Create new project object matching the Project interface
    const newProject = {
      id: `project-${Date.now()}`,
      name: projectName.trim(),
      description: projectDescription.trim() || undefined,
      organizationId: 'org-1',
      dealId: selectedDealForProject.id,
      ownerId: '1', // Mock user ID - in real app this would be current user
      createdBy: '1', // Mock user ID - in real app this would be current user
      createdDate: new Date().toISOString(),
      updatedBy: '1', // Mock user ID - in real app this would be current user
      updatedDate: new Date().toISOString(),
      lifecycle: ProjectLifecycle.InProcess,
      stage: ProjectStage.Planning,
      tags: [selectedDealForProject.dealType.toLowerCase().replace(' ', '-')],
      isPrivate: false,
      teamMembers: [],
      files: [],
      folders: [],
      queries: [],
      fileCount: 0,
      queryCount: 0,
      totalSize: 0,
      priority: ProjectPriority.Medium, // Default priority
      sharedWith: [],
    };

    // Save project to local storage
    try {
      addProjectToStorage(newProject);
      
      // Update deal to mark it as having an associated project
      updateDealInStorage(selectedDealForProject.id, {
        hasProject: true,
        projectId: newProject.id,
        projectName: newProject.name,
      });

      // Close dialog and reset state
      setCreateProjectDialogOpen(false);
      setProjectName('');
      setProjectDescription('');
      setSelectedDealForProject(null);

      // Navigate to projects page to show the new project
      navigate('/projects');
    } catch (error) {
      console.error('Error creating project:', error);
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Error creating project. Please try again.' }));
    }
  };

  const handleCreateDealSubmit = async (dealData: any) => {
    try {
      const newDeal = await createDeal(dealData);
      
      // Add the new deal to the local state immediately so it shows up in the UI
      setDeals(prevDeals => [newDeal, ...prevDeals]);
      
      // Return the created deal so CreateDealDialog can access the ID for document processing
      return newDeal;
    } catch (error) {
      console.error('Failed to create deal:', error);
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Error creating deal. Please try again.' }));
      throw error; // Re-throw so CreateDealDialog can handle the error
    }
  };

  const handleCreateProjectCancel = () => {
    setCreateProjectDialogOpen(false);
    setProjectName('');
    setProjectDescription('');
    setSelectedDealForProject(null);
  };

  const handleDeleteDeal = (dealId: string) => {
    setDealToDelete(dealId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!dealToDelete) return;

    setIsDeleting(true);
    const originalDeals = [...deals];
    setDeals(deals.filter(deal => deal.id !== dealToDelete));
    if (selectedDeal === dealToDelete) {
      setSelectedDeal(null);
    }

    try {
      await deleteDeal(dealToDelete);
    } catch (error) {
      console.error('Failed to delete deal:', error);
      setDeals(originalDeals);
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Error deleting deal. Please try again.' }));
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setDealToDelete(null);
    }
  };

  // Handle converting deal to project using the new API
  const handleConvertToProject = async (deal: any) => {
    try {
      setConvertingDeals(prev => new Set(prev).add(deal.id));
      
      const token = localStorage.getItem('token');
      const organization = localStorage.getItem('organization');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      if (organization) {
        const org = JSON.parse(organization);
        headers['X-Organization-ID'] = org.id;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/deals/${deal.id}/convert-to-project`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          include_documents: true,
          include_financials: true,
          include_scoring: true
        }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Navigate to the new project
        navigate(`/projects/${result.project_id}`);
      } else {
        throw new Error(`Conversion failed: ${response.status}`);
      }
    } catch (err) {
      console.error('Error converting deal to project:', err);
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Error converting deal to project. Please try again.' }));
    } finally {
      setConvertingDeals(prev => {
        const newSet = new Set(prev);
        newSet.delete(deal.id);
        return newSet;
      });
    }
  };


  const clearAllFilters = () => {
    setSearch('');
    setDealType('');
    setStatus('');
  };

  const hasActiveFilters = search || dealType || status;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Paper sx={{ mb: 3, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Deal Analysis</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ ml: 1 }}
            onClick={() => setCreateDealDialogOpen(true)}
          >
            Create Deal
          </Button>
        </Box>
      </Paper>

      <CreateDealDialog
        open={createDealDialogOpen}
        onClose={() => setCreateDealDialogOpen(false)}
        onSubmit={handleCreateDealSubmit}
      />

      <Paper sx={{ width: '100%', height: '85vh', display: 'flex', flexDirection: 'column', p: 3 }}>
        <Box sx={{ pb: 3 }}> {/* Added padding-bottom to separate filters from scrollable content */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search by name, description, company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 300,
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <Tooltip title="Toggle Filters">
                <IconButton
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  color={hasActiveFilters ? 'primary' : 'default'}
                  sx={{
                    border: hasActiveFilters ? '2px solid' : '1px solid',
                    borderColor: hasActiveFilters ? 'primary.main' : 'divider',
                  }}
                >
                  <Badge badgeContent={hasActiveFilters ? '!' : 0} color="error">
                    <FilterListIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem />

              <ToggleButtonGroup
                value={sort}
                exclusive
                onChange={(e, value) => {
                  if (value) {
                    const isAsc = sort.endsWith('asc');
                    setSort(isAsc ? value.replace('asc', 'desc') as DealSort : value.replace('desc', 'asc') as DealSort);
                  }
                }}
                size="small"
              >
                <ToggleButton value={DealSort.UPDATED_ASC}>
                  <Tooltip title="Sort by Last Updated">
                    <ScheduleIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={DealSort.NAME_ASC}>
                  <Tooltip title="Sort by Name">
                    <TagIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>

              <IconButton
                onClick={() => {
                  const isAsc = sort.endsWith('asc');
                  setSort(isAsc ? sort.replace('asc', 'desc') as DealSort : sort.replace('desc', 'asc') as DealSort);
                }}
                color="primary"
              >
                <Tooltip title={`Sort ${sort.endsWith('asc') ? 'Descending' : 'Ascending'}`}>
                  <SortIcon sx={{ transform: sort.endsWith('desc') ? 'rotate(180deg)' : 'none' }} />
                </Tooltip>
              </IconButton>

              <Divider orientation="vertical" flexItem />

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, value) => value && setViewMode(value)}
                size="small"
              >
                <ToggleButton value="grid">
                  <Tooltip title="Grid View">
                    <GridViewIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="list">
                  <Tooltip title="List View">
                    <ListViewIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {filtersOpen && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Deal Type</InputLabel>
                    <Select
                      value={dealType}
                      onChange={(e) => setDealType(e.target.value as DealType | '')}
                      label="Deal Type"
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      {Object.values(DealType).map((type) => (
                        <MenuItem key={type} value={type}>
                          {toTitleCase(type)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as DealStatus | '')}
                      label="Status"
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      {Object.values(DealStatus).map((s) => (
                        <MenuItem key={s} value={s}>
                          {toTitleCase(s)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {hasActiveFilters && (
                    <Button
                      startIcon={<ClearIcon />}
                      onClick={clearAllFilters}
                      size="small"
                      color="error"
                      variant="outlined"
                    >
                      Clear Filters
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, pt: 0 }}> {/* Added p:3 and pt:0 for consistent padding */}
          <Typography variant="h6" gutterBottom>Deals</Typography>
          {loading ? (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <DealCardSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : (
            <>
              {deals.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No Deals Created Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create your first deal to start tracking and analyzing opportunities.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDealDialogOpen(true)}
                  >
                    Create New Deal
                  </Button>
                </Box>
              ) : viewMode === 'grid' ? (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {deals.map((deal) => (
                    <Grid item xs={12} sm={6} md={4} key={deal.id}>
                      <DealCard
                        deal={deal}
                        selected={selectedDeal === deal.id}
                        converting={convertingDeals.has(deal.id)}
                        onSelect={handleDealSelect}
                        onViewDetails={(dealId) => navigate(`/deals/${dealId}`)}
                        onConvertToProject={handleConvertToProject}
                        onDelete={handleDeleteDeal}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Stack spacing={2}>
                  {deals.map((deal) => (
                    <Fade in timeout={200} key={deal.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          border: selectedDeal === deal.id ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.12)',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateX(4px)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                          }
                        }}
                        onClick={() => handleDealSelect(deal.id)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {deal.name}
                              </Typography>
                              <Chip label={deal.status} color="primary" size="small" />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {deal.company || 'N/A'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: '400px', justifyContent: 'space-between' }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" color="text.secondary">Industry</Typography>
                              <Typography variant="body2">{deal.industry || 'N/A'}</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" color="text.secondary">Value</Typography>
                              <Typography variant="body2">{deal.estimatedValue ? `${deal.currency} ${deal.estimatedValue / 1000000}M` : 'N/A'}</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" color="text.secondary">Source</Typography>
                              <Typography variant="body2">{deal.source || 'N/A'}</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" color="text.secondary">Date Added</Typography>
                              <Typography variant="body2">{new Date(deal.created_at).toLocaleDateString()}</Typography>
                            </Box>
                          </Box>

                          <CardActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<BusinessIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/deals/${deal.id}`);
                              }}
                            >
                              View Details
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={convertingDeals.has(deal.id) ? <CircularProgress size={16} /> : <TransformIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConvertToProject(deal);
                              }}
                              disabled={convertingDeals.has(deal.id)}
                            >
                              {convertingDeals.has(deal.id) ? 'Converting...' : 'Convert to Project'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDeal(deal.id);
                              }}
                            >
                              Delete
                            </Button>
                          </CardActions>
                        </Box>
                      </Card>
                    </Fade>
                  ))}
                </Stack>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Create Project Dialog */}
      <Dialog open={createProjectDialogOpen} onClose={handleCreateProjectCancel} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon color="primary" />
          Create Project from Deal
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {selectedDealForProject && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Associated Deal:</strong> {selectedDealForProject.name} ({selectedDealForProject.location}) - {selectedDealForProject.price}
                </Typography>
              </Alert>
            )}

            <TextField
              label="Project Name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              fullWidth
              required
              helperText="This project will be automatically associated with the selected deal"
            />

            <TextField
              label="Project Description"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe the scope and objectives of this project..."
            />

            <Alert severity="success">
              <Typography variant="body2">
                <strong>What happens next:</strong>
                <br />
                • A new project will be created in your Vault
                <br />
                • The project will be automatically linked to this deal
                <br />
                • You can upload documents, create queries, and manage the due diligence process
                <br />
                • Team members can be added to collaborate on the project
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateProjectCancel}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateProjectSubmit}
            disabled={!projectName.trim()}
            startIcon={<AssignmentIcon />}
          >
            Create Project
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this deal? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isDeleting}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DealSourcing;
